import { FC, useEffect, useState } from 'react';
import { findLastIndex, isNil } from 'lodash';
import styles from './FamilyBooking.module.scss';
import { MYSELF_AND_OTHERS_OPTION, ModeOption } from '../utils/constants';
import cx from 'classnames';
import useIsLoadedInsideIframe from 'hooks/useIsLoadedInsideIframe';
import BookingModeSelector from '../BookingModeSelector/BookingModeSelector';
import { useAppDispatch, useAppSelector } from 'redux/reduxHooks';
import {
  STEPS,
  clearBookingPatient,
  pushFamilyStep,
  resetContactUserSchedule,
  resetFamilyBooking,
  updateContactPatient,
  updateFBStep1,
  updatePatients,
} from 'redux/familyBookingSlice';
import { getFamilyBookingPatientsAPI } from 'services/APIs';
import { batch } from 'react-redux';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import { IClinicServicesOption } from '../BookNowPanel';
import HeaderSection from '../HeaderSection/HeaderSection';
import useIsMobile from 'hooks/useIsMobile';
import { isAllowedToAddPatient } from './utils/utils';
import ReviewAppointment from './ReviewAppointment/ReviewAppointment';
import FamilyBookingStep from './FamilyBookingStep/FamilyBookingStep';
import { FAMILY_STEP_CONFIGS } from './steps';

const { CONTACT_DETAILS } = STEPS;

interface FamilyBookingProps {
  mode: ModeOption;
  setMode: React.Dispatch<React.SetStateAction<ModeOption>>;
  clinicServicesOptions: IClinicServicesOption[];
}

const FamilyBooking: FC<FamilyBookingProps> = ({
  mode,
  setMode,
  clinicServicesOptions,
}) => {
  const {
    familyBookingSlice: { stepStack, patients, isReviewOpen },
    authSlice: { isLoggedIn, userProfile },
    clinicDetailsSlice: { clinicData },
  } = useAppSelector((state) => state);

  const isLoadedInsideIframe = useIsLoadedInsideIframe();
  const [isLoading, setIsLoading] = useState(isLoggedIn);

  const isMobile = useIsMobile();

  const dispatch = useAppDispatch();

  useEffect(() => {
    return () => {
      dispatch(resetFamilyBooking());
    };
  }, [dispatch]);

  useEffect(() => {
    const getContactUserAndPatients = async () => {
      if (!isLoggedIn || !userProfile || !clinicData) return;
      setIsLoading(true);
      try {
        const patients = await getFamilyBookingPatientsAPI(
          {
            clinicId: clinicData.id,
            dob: userProfile.dob,
            phoneNumber: userProfile.phoneNumber,
          },
          {}
        );

        batch(() => {
          dispatch(updatePatients(patients));

          dispatch(
            updateFBStep1({
              phoneNumber: userProfile.phoneNumber,
              dob: userProfile.dob,
            })
          );

          dispatch(
            updateContactPatient({
              email: userProfile.email,
              firstName: userProfile.firstName,
              lastName: userProfile.lastName,
            })
          );

          dispatch(pushFamilyStep(CONTACT_DETAILS));
        });
      } finally {
        setIsLoading(false);
      }
    };
    getContactUserAndPatients();
  }, [clinicData, dispatch, isLoggedIn, userProfile]);

  const onChangeMode = (newMode: ModeOption) => {
    if (newMode.id === MYSELF_AND_OTHERS_OPTION.id) {
      const isAllowedToAddMore = isAllowedToAddPatient({ mode, patients });

      if (!isAllowedToAddMore) {
        const lastBookingPatientIndex = findLastIndex(
          patients,
          (item) => !isNil(item.serviceId)
        );

        dispatch(clearBookingPatient(patients[lastBookingPatientIndex]));
      }
    } else {
      dispatch(resetContactUserSchedule());
    }

    setMode(newMode);
  };

  const currentStep = stepStack[stepStack.length - 1];

  const { title, showsModeDropdown, allowsOverflow } =
    FAMILY_STEP_CONFIGS[currentStep];

  const isOverflowAllowed =
    allowsOverflow === 'always' || (allowsOverflow === 'mobile' && isMobile);

  const containerClassname = cx({
    [styles['container']]: true,
    [styles['container-iframe']]: isLoadedInsideIframe,
    [styles['revert-overflow']]: isOverflowAllowed,
  });

  return (
    <div className={containerClassname}>
      {isLoading && <LoadingScreen />}

      {isReviewOpen && (
        <ReviewAppointment mode={mode} />
      )}

      <HeaderSection
        currentStep={currentStep}
        title={title}
      >
        {showsModeDropdown ? (
          <div className={styles['dropdown-section']}>
            <BookingModeSelector value={mode} onChange={onChangeMode} />
          </div>
        ) : (
          <div>{mode.label}</div>
        )}
      </HeaderSection>

      <FamilyBookingStep
        currentStep={currentStep}
        mode={mode}
        clinicServicesOptions={clinicServicesOptions}
      />
    </div>
  );
};

export default FamilyBooking;
